import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Search, ExternalLink, MapPin, CalendarDays, Users, Sparkles, Compass, Target, Trophy } from "lucide-react";

const TABS = [
  { id: "leads", label: "Paid client leads", icon: Users },
  { id: "future", label: "Tomorrows Job For You", icon: Sparkles },
  { id: "direction", label: "Closest direction", icon: Compass },
];

const STATUS_STYLE = {
  new: "border-slate-300 bg-white text-slate-600",
  contacted: "border-sky-300 bg-sky-50 text-sky-700",
  replied: "border-violet-300 bg-violet-50 text-violet-700",
  won: "border-emerald-300 bg-emerald-50 text-emerald-700",
  lost: "border-slate-300 bg-slate-100 text-slate-500",
};

const leadKey = (url) => {
  let hash = 0;
  for (let i = 0; i < url.length; i += 1) hash = (hash * 31 + url.charCodeAt(i)) >>> 0;
  return hash.toString(36);
};

const LeadCard = ({ lead, symbol, onStatus, saving }) => {
  const [wonAmount, setWonAmount] = useState(lead.won_amount || "");
  const [askWon, setAskWon] = useState(false);
  const status = lead.status || "new";
  const key = leadKey(lead.url);

  return (
    <div className="card-soft p-4" data-testid={`lead-card-${key}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="font-semibold text-slate-900 text-sm">{lead.title}</p>
        <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600">{lead.source}</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Posted {lead.posted_date} ({lead.age_days}d ago)</span>
        <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {lead.location || "Not stated"}</span>
        {lead.budget && <span className="inline-flex items-center gap-1"><Target className="w-3 h-3" /> {lead.budget}</span>}
        <span className={`rounded-full px-2 py-0.5 ${lead.contact_mode === "in_person" ? "bg-amber-50 text-amber-700" : "bg-sky-50 text-sky-700"}`}>
          {lead.contact_mode === "in_person" ? "Approach in person" : "Approach online"}
        </span>
      </div>
      {lead.engagement === "full_time_role" && (
        <p className="mt-2 text-[11px] text-amber-700" data-testid="lead-full-time-flag">{lead.client_type}</p>
      )}
      {lead.fit_note && <p className="mt-2.5 text-sm text-slate-700">{lead.fit_note}</p>}
      {lead.approach && (
        <p className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600"><span className="font-semibold text-slate-700">How to connect: </span>{lead.approach}</p>
      )}
      {lead.first_message && (
        <details className="mt-2" data-testid="lead-first-message">
          <summary className="cursor-pointer text-xs font-semibold text-sky-700">See a first message you could send</summary>
          <p className="mt-1.5 rounded-lg border border-sky-100 bg-sky-50 p-3 text-xs leading-relaxed text-slate-700">{lead.first_message}</p>
        </details>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <a href={lead.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-700 hover:text-sky-900" data-testid={`lead-open-link-${key}`}>
          Open the actual listing <ExternalLink className="w-3 h-3" />
        </a>
        <span className="text-slate-300">·</span>
        {["contacted", "replied", "won", "lost"].map((s) => (
          <button key={s} disabled={saving}
            onClick={() => (s === "won" ? (status === "won" ? onStatus(lead, "new") : setAskWon(true)) : onStatus(lead, status === s ? "new" : s))}
            data-testid={`lead-status-${s}-${key}`}
            className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold capitalize transition-colors ${status === s ? STATUS_STYLE[s] : "border-slate-200 bg-white text-slate-500 hover:border-slate-400"}`}>
            {s}
          </button>
        ))}
        {status !== "new" && (
          <span className={`rounded-full border px-2 py-0.5 text-[11px] ${STATUS_STYLE[status]}`} data-testid={`lead-current-status-${key}`}>
            Marked {status}{status === "won" && lead.won_amount ? ` · ${symbol}${Number(lead.won_amount).toLocaleString()}` : ""}
          </span>
        )}
      </div>

      {askWon && (
        <div className="mt-3 flex flex-wrap items-end gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid={`lead-won-form-${key}`}>
          <label className="text-xs text-slate-600">How much are you being paid ({symbol})?
            <input type="number" min={0} value={wonAmount} onChange={(e) => setWonAmount(e.target.value)}
              className="mt-1 w-40 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-emerald-300 focus:outline-none" data-testid={`lead-won-amount-${key}`} />
          </label>
          <Button onClick={() => { onStatus(lead, "won", Number(wonAmount) || 0); setAskWon(false); }} disabled={saving || !(Number(wonAmount) > 0)}
            className="h-9 bg-emerald-600 hover:bg-emerald-500 text-white" data-testid={`lead-won-confirm-${key}`}>Mark as won</Button>
          <button onClick={() => setAskWon(false)} className="text-xs text-slate-500 py-2">Cancel</button>
        </div>
      )}
    </div>
  );
};

const Progress = ({ tracker, symbol }) => (
  <div className="accent-bar card-soft p-4 pl-5" style={{ "--accent": "#059669" }} data-testid="outreach-tracker">
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <p className="inline-flex items-center gap-2 text-sm font-bold text-slate-900"><Trophy className="w-4 h-4 text-emerald-600" /> Your outreach so far</p>
      <p className="num-display text-sm text-slate-700" data-testid="tracker-amount">
        {symbol}{Number(tracker.won_amount || 0).toLocaleString()} won of {symbol}{Number(tracker.target || 0).toLocaleString()}
      </p>
    </div>
    <div className="mt-2.5 h-2 w-full overflow-hidden rounded-full bg-slate-100">
      <div className="h-full rounded-full bg-emerald-500 transition-all duration-500" style={{ width: `${tracker.percent || 0}%` }} data-testid="tracker-bar" />
    </div>
    <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-600" data-testid="tracker-counts">
      <span>Contacted {tracker.counts?.contacted || 0}</span>
      <span>Replied {tracker.counts?.replied || 0}</span>
      <span>Won {tracker.counts?.won || 0}</span>
      <span>Not now {tracker.counts?.lost || 0}</span>
    </div>
    <p className="mt-2 text-[11px] text-slate-500">Only what you mark yourself counts here — nothing is assumed from a click.</p>
  </div>
);

export const ClientLeads = ({ state, symbol = "₹" }) => {
  const [location, setLocation] = useState("");
  const [keywords, setKeywords] = useState("");
  const [run, setRun] = useState(null);
  const [tracker, setTracker] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("leads");

  useEffect(() => {
    api.get("/accelerator/leads").then(({ data }) => {
      if (data.location) setLocation(data.location);
      if (data.run) setRun(data.run);
      if (data.tracker) setTracker(data.tracker);
    }).catch((e) => toast.error(e?.response?.data?.detail || "Could not load your last search"));
  }, []);

  const search = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/accelerator/leads", { location, keywords });
      setRun(data.run);
      setTracker(data.tracker);
      setTab(data.run.leads?.length ? "leads" : "future");
      toast.success(data.run.leads?.length ? `${data.run.leads.length} live leads found` : "No strong live leads right now — see the other tabs");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not run the search just now");
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (lead, status, amount) => {
    setSaving(true);
    try {
      const { data } = await api.post("/accelerator/leads/track", {
        url: lead.url, title: lead.title, source: lead.source, status, amount: amount ?? null,
      });
      setTracker(data.tracker);
      setRun((prev) => ({ ...prev, leads: (prev.leads || []).map((l) => l.url === lead.url ? { ...l, status, won_amount: status === "won" ? (amount || 0) : 0 } : l) }));
      toast.success(status === "new" ? "Status cleared" : `Marked ${status}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save that status");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="space-y-4" data-testid="client-leads-panel">
      <div className="card-soft p-5 space-y-3">
        <div>
          <h3 className="text-base md:text-lg font-bold text-slate-900">Find me my 1st paid client</h3>
          <p className="text-sm text-slate-600 mt-1">We search live public listing APIs for real, open work that matches your chosen direction — including clients posting from India. Every lead links to the actual listing with its real posted date.</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="block text-xs text-slate-500 sm:col-span-1">Your city / area
            <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Pune, India"
              className="w-full mt-1.5 rounded-lg bg-slate-50 border border-slate-300 px-3 py-2.5 text-base sm:text-sm text-slate-900 focus:border-sky-200 focus:outline-none" data-testid="leads-location-input" />
          </label>
          <label className="block text-xs text-slate-500 sm:col-span-2">Extra keywords (optional)
            <input value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="e.g. supply chain audit"
              className="w-full mt-1.5 rounded-lg bg-slate-50 border border-slate-300 px-3 py-2.5 text-base sm:text-sm text-slate-900 focus:border-sky-200 focus:outline-none" data-testid="leads-keywords-input" />
          </label>
        </div>
        <Button onClick={search} disabled={busy} className="h-12 w-full sm:w-auto bg-sky-600 hover:bg-sky-700 text-white" data-testid="find-first-client-button">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />} {busy ? "Searching live listings…" : "Search live listings"}
        </Button>
      </div>

      {tracker && (tracker.target > 0) && <Progress tracker={tracker} symbol={symbol} />}

      {run && (
        <>
          <nav className="-mx-4 px-4 overflow-x-auto sm:mx-0 sm:px-0">
            <div className="flex gap-2 min-w-max" data-testid="leads-tabs">
              {TABS.map(({ id, label, icon: Icon }) => (
                <button key={id} onClick={() => setTab(id)} data-testid={`leads-tab-${id}`}
                  className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-xs transition-colors ${tab === id ? "border-sky-200 bg-sky-50 text-sky-700" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"}`}>
                  <Icon className="w-3.5 h-3.5" /> {label}
                  {id === "leads" && <span className="text-slate-400">{run.leads?.length || 0}</span>}
                  {id === "future" && <span className="text-slate-400">{run.tomorrows_jobs?.length || 0}</span>}
                </button>
              ))}
            </div>
          </nav>

          {tab === "leads" && (
            <div className="space-y-3" data-testid="leads-tab-panel-leads">
              {run.note && <p className="text-xs text-slate-600 rounded-lg border border-slate-200 bg-white p-3" data-testid="leads-note">{run.note}</p>}
              {run.leads?.length
                ? run.leads.map((lead) => <LeadCard key={lead.url} lead={lead} symbol={symbol} saving={saving} onStatus={setStatus} />)
                : <p className="card-soft p-5 text-sm text-slate-600" data-testid="leads-empty">
                    No open listing matched your direction closely enough in the last 15 days. Nothing invented to fill the gap — open the other two tabs for the honest alternatives.
                  </p>}
              <p className="text-[11px] text-slate-500" data-testid="leads-coverage">{run.coverage} Searched: {(run.terms || []).join(" · ")}</p>
            </div>
          )}

          {tab === "future" && (
            <div className="space-y-3" data-testid="leads-tab-panel-future">
              <p className="text-sm text-slate-600">Your experience mapped onto the published trending future-job lists — the three closest roles for you.</p>
              {run.tomorrows_jobs?.length
                ? run.tomorrows_jobs.map((role) => (
                    <div key={role.id} className="card-soft p-4" data-testid={`tomorrows-job-${role.id}`}>
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <p className="font-semibold text-slate-900 text-sm">{role.title}</p>
                        <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700">Fit {role.score} · {role.verdict}</span>
                      </div>
                      {role.why_now && <p className="mt-2 text-sm text-slate-700">{role.why_now}</p>}
                      {!!role.matched_skills?.length && <p className="mt-2 text-xs text-emerald-700">You already have: {role.matched_skills.join(", ")}</p>}
                      {!!role.missing_skills?.length && <p className="mt-1 text-xs text-amber-700">Still to bridge: {role.missing_skills.join(", ")}</p>}
                    </div>
                  ))
                : <p className="card-soft p-5 text-sm text-slate-600" data-testid="tomorrows-jobs-empty">Analyse a resume first so we can map your experience onto the future-job lists.</p>}
            </div>
          )}

          {tab === "direction" && (
            <div className="space-y-3" data-testid="leads-tab-panel-direction">
              <p className="text-sm text-slate-600">If the live market is quiet for this direction, the closest directions you already generated are below. You can switch to any of them on the Your Direction step.</p>
              <div className="card-soft p-4" data-testid="direction-current">
                <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500">Your current direction</p>
                <p className="mt-1.5 text-sm font-semibold text-slate-900">{run.direction?.current?.title}</p>
                <p className="mt-1 text-sm text-slate-700">{run.direction?.current?.offer}</p>
              </div>
              {run.direction?.alternates?.length
                ? run.direction.alternates.map((alt) => (
                    <div key={alt.id} className="card-soft p-4" data-testid={`direction-alternate-${alt.id}`}>
                      <p className="font-semibold text-slate-900 text-sm">{alt.title}</p>
                      <p className="mt-1.5 text-sm text-slate-700">{alt.offer}</p>
                      {alt.how_to_test && <p className="mt-2 text-xs text-slate-600"><span className="font-semibold">Smallest test: </span>{alt.how_to_test}</p>}
                      {!!alt.validation_channels?.length && <p className="mt-1 text-xs text-slate-500">Where to test: {alt.validation_channels.join(", ")}</p>}
                    </div>
                  ))
                : <p className="card-soft p-5 text-sm text-slate-600" data-testid="direction-empty">You have no other saved directions yet — generate more on the Income Routes step.</p>}
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default ClientLeads;
